// Página /admin/padrao-visual — vitrine do padrão visual MPires.
// Exclusiva de Administrador. Serve como referência; nenhuma tela existente
// consome os componentes mpires/* automaticamente neste lote.
import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MpButton, MpStatusChip, MpAlert, MpCard, tokens } from "@/components/mpires";
import { Plus, Pencil, Trash2 } from "@/components/icons";

const Section = ({ title, children }) => (
  <section style={{ marginBottom: 32 }}>
    <h2 style={{
      fontSize: 20, fontWeight: 600, color: tokens.colors.marinho,
      margin: "0 0 12px 0", borderBottom: `1px solid ${tokens.colors.neutro[300]}`,
      paddingBottom: 8,
    }}>{title}</h2>
    {children}
  </section>
);

const Row = ({ children }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>{children}</div>
);

const Swatch = ({ name, value }) => (
  <div style={{ width: 160 }}>
    <div style={{ height: 48, background: value, borderRadius: 8, border: `1px solid ${tokens.colors.neutro[300]}` }} />
    <div style={{ fontSize: 12, color: tokens.colors.neutro[600], marginTop: 4 }}>
      <div style={{ fontWeight: 600, color: tokens.colors.marinho }}>{name}</div>
      <div>{value}</div>
    </div>
  </div>
);

export default function PadraoVisual() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div style={{ maxWidth: 960, fontFamily: tokens.typography.familia }} data-testid="padrao-visual-page">
      <header style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: tokens.colors.marinho, margin: 0 }}>
          Padrão visual MPires
        </h1>
        <p style={{ fontSize: 14, color: tokens.colors.neutro[600], marginTop: 4 }}>
          Referência interna de componentes. Esta página existe só para validação visual
          antes da adoção progressiva — nenhuma tela existente usa estes componentes ainda.
        </p>
      </header>

      <Section title="Cores">
        <Row>
          <Swatch name="Marinho (estrutura)" value={tokens.colors.marinho} />
          <Swatch name="Primária (ação/link)" value={tokens.colors.primaria} />
          <Swatch name="Neutro 100" value={tokens.colors.neutro[100]} />
          <Swatch name="Neutro 300" value={tokens.colors.neutro[300]} />
          <Swatch name="Neutro 600" value={tokens.colors.neutro[600]} />
          <Swatch name="Vermelho (estado)" value={tokens.colors.vermelho} />
          <Swatch name="Âmbar (estado)" value={tokens.colors.ambar} />
          <Swatch name="Verde (estado)" value={tokens.colors.verde} />
        </Row>
      </Section>

      <Section title="Tipografia (Inter)">
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 28, fontWeight: 600, color: tokens.colors.marinho }}>Display 28 / 600</div>
          <div style={{ fontSize: 24, fontWeight: 600, color: tokens.colors.marinho }}>XL 24 / 600</div>
          <div style={{ fontSize: 20, fontWeight: 600, color: tokens.colors.marinho }}>LG 20 / 600</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: tokens.colors.marinho }}>MD 16 / 600</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: tokens.colors.marinho }}>SM 14 / 500</div>
          <div style={{ fontSize: 12, fontWeight: 400, color: tokens.colors.neutro[600] }}>XS 12 / 400</div>
        </div>
      </Section>

      <Section title="MpButton — tamanhos">
        <Row>
          <MpButton size="P" type="primary" icon={Plus} testId="mp-btn-p-primary">Primário P</MpButton>
          <MpButton size="M" type="primary" icon={Plus} testId="mp-btn-m-primary">Primário M</MpButton>
          <MpButton size="G" type="primary" icon={Plus} testId="mp-btn-g-primary">Primário G</MpButton>
        </Row>
      </Section>

      <Section title="MpButton — tipos">
        <Row>
          <MpButton type="primary" icon={Plus}>Primário</MpButton>
          <MpButton type="secondary" icon={Pencil}>Secundário</MpButton>
          <MpButton type="tertiary">Terciário</MpButton>
          <MpButton type="destructive" icon={Trash2}>Destrutivo</MpButton>
          <MpButton type="primary" disabled icon={Plus}>Desabilitado</MpButton>
        </Row>
      </Section>

      <Section title="MpStatusChip">
        <Row>
          <MpStatusChip status="prospect" />
          <MpStatusChip status="cliente" />
          <MpStatusChip status="ganho" />
          <MpStatusChip status="perdido" />
          <MpStatusChip status="atrasada" />
          <MpStatusChip status="hoje" />
          <MpStatusChip status="parada_7d" />
          <MpStatusChip status="parada_15d" />
          <MpStatusChip status="pulada" />
          <MpStatusChip status="cross" />
        </Row>
      </Section>

      <Section title="MpAlert">
        <div style={{ display: "grid", gap: 12 }}>
          <MpAlert level="info" title="Informação" action={<MpButton size="P" type="tertiary">Ver</MpButton>}>
            Texto curto de contexto.
          </MpAlert>
          <MpAlert level="warn" title="Atenção">Algo que precisa de olhar.</MpAlert>
          <MpAlert level="danger" title="Crítico">Algo que precisa de ação imediata.</MpAlert>
        </div>
      </Section>

      <Section title="MpCard">
        <div style={{ display: "grid", gap: 12 }}>
          <MpCard title="Cartão simples" action={<MpButton size="P" type="tertiary">Ação</MpButton>}>
            <div style={{ fontSize: 14, color: tokens.colors.neutro[600] }}>
              Conteúdo neutro do cartão. Fundo branco, borda 1 px, raio 12.
            </div>
          </MpCard>
          <MpCard title="Com selo" action={<MpStatusChip status="hoje" />}>
            <div style={{ fontSize: 14, color: tokens.colors.neutro[600] }}>
              Combinação de cartão + selo de estado.
            </div>
          </MpCard>
        </div>
      </Section>

      <p style={{ fontSize: 12, color: tokens.colors.neutro[600], marginTop: 32 }}>
        Esta página é somente leitura. Validação prevista: desktop e 375 px.
      </p>
    </div>
  );
}
